import {
  type AnyRouter,
  type ParsedLocation,
  useRouter,
} from "@tanstack/react-router";
import { usePostHog } from "posthog-js/react";
import { useEffect } from "react";
import { isMarketingDocument } from "@/routing/marketingDocument";
import { PostHogEvents, useEvents } from "@/tracking/useEvents";
import { DEFAULT_CUSTOMIZE_PATHNAME } from "@/utils/formHref";

/**
 * The address each router last counted a page view for. The router reports
 * `onResolved` again for the page it is already on whenever its pending state
 * settles — every viewport preload, loader update and invalidation — and each
 * of those was a page view (R-2264). Kept per router, not per hook, so a
 * re-run of the effect counts nothing it has already counted.
 */
const lastPageAddresses = new WeakMap<AnyRouter, string>();

/** Customize sends its own page view, with the plan; arriving at login or register by a link was never counted. */
const UNCOUNTED_PATHS = ["/login", "/register", DEFAULT_CUSTOMIZE_PATHNAME];

/** The location's address when it is a page not yet counted, else undefined. */
function newPageAddress(router: AnyRouter, location: ParsedLocation) {
  const address = `${location.pathname}${location.searchStr}`;
  if (lastPageAddresses.get(router) === address) return undefined;
  lastPageAddresses.set(router, address);
  return address;
}

/** How long a landing waits for the visitor's flags before it counts without them. */
const FLAGS_WAIT_MS = 3000;

/**
 * Runs `count` once, when the visitor's flags arrive. It runs anyway after
 * `FLAGS_WAIT_MS`, or as soon as the visitor leaves the page, so a slow or
 * missing `/flags` answer or a quick bounce still counts the page. A client
 * not started yet (analytics refused) only counts once it starts and loads them.
 */
function afterFlags(posthog: ReturnType<typeof usePostHog>, count: () => void) {
  const cleanups: (() => void)[] = [];
  let counted = false;
  const once = () => {
    if (counted) return;
    counted = true;
    cleanups.forEach((cleanup) => cleanup());
    count();
  };
  if (posthog.__loaded) {
    const timer = setTimeout(once, FLAGS_WAIT_MS);
    const onHidden = () => {
      if (document.visibilityState === "hidden") once();
    };
    document.addEventListener("visibilitychange", onHidden);
    cleanups.push(
      () => clearTimeout(timer),
      () => document.removeEventListener("visibilitychange", onHidden)
    );
  }
  // Called at once when the flags are already loaded.
  const stopListening = posthog.onFeatureFlags(once);
  if (counted) stopListening();
  else cleanups.push(stopListening);
}

/** Counts a `$pageview` for the page landed on and for each page navigated to after it. */
export default function usePageViews() {
  const { captureEvent } = useEvents();
  const posthog = usePostHog();
  const router = useRouter();

  useEffect(() => {
    // Counted here rather than on the window's `load`, which can fire before
    // hydration has run this effect.
    if (newPageAddress(router, router.state.location)) {
      const url = window.location.href;
      const count = () =>
        captureEvent(PostHogEvents.PageView, { $current_url: url });
      // A marketing page waits for the visitor's flags, so the page view
      // carries the experiments they are in, as GTM's tag sent it. The
      // client can also start later there, when a visitor who refused
      // analytics accepts it. Its links load a new document, so no
      // navigation is counted ahead of it.
      if (isMarketingDocument()) afterFlags(posthog, count);
      else count();
    }

    return router.subscribe("onResolved", ({ toLocation }) => {
      const url = newPageAddress(router, toLocation);
      if (url && !UNCOUNTED_PATHS.includes(toLocation.pathname)) {
        captureEvent(PostHogEvents.PageView, { $current_url: url });
      }
    });
  }, [router, captureEvent, posthog]);
}
